'use client'

import React, {useCallback} from 'react'
import type {Filter} from '../types'
import type {SelectedFilter} from '@/src/core/domain/entities/catalog/filters'
import {FiltersTitleIcon} from '@/src/features/catalog/filters-v2/ui/title-icon'
import {CloseButton} from '@/src/shared/ui/close-button'
import * as UI from './filters.styled'
import {X} from 'lucide-react'
import Button from '@/src/shared/ui/button'
import {observer} from 'mobx-react'
import {useFilters} from '@/src/features/catalog/filters-v2/containers/csr-filters-provider'
import {useRouter} from 'nextjs-toploader/app'
import {usePathname, useSearchParams} from 'next/navigation'
import {navigateToFilters} from '../utils/navigate'

export interface FiltersPanelProps {
  filters: (Filter | null)[]
  selectedFilters: SelectedFilter[]
  mobileVisible: boolean
  onFilterChange: (code: string, value: unknown) => void
  onClearAll: () => void
}

export function FiltersPanel({
  filters,
  selectedFilters,
  mobileVisible,
  onFilterChange,
  onClearAll,
}: FiltersPanelProps) {
  const nonNullFilters = filters.filter((f): f is Filter => f !== null)

  const rangeFilters = nonNullFilters.filter(f => f.type === 'range')
  const checkboxFilters = nonNullFilters.filter(f => f.type === 'checkbox')
  const otherFilters = nonNullFilters.filter(f => f.type !== 'checkbox' && f.type !== 'range')

  return (
    <UI.Wrapper $visible={mobileVisible}>
      <UI.Container>
        {nonNullFilters.length > 0 && (
          <>
            <UI.HeadTitle>
              <FiltersTitleIcon />
              <UI.HeadTitleText>Фильтры</UI.HeadTitleText>
              {selectedFilters.length > 0 && (
                <CloseButton active={selectedFilters.length > 0} onClick={onClearAll} />
              )}
            </UI.HeadTitle>

            <UI.FiltersList>
              {rangeFilters.map(filter => (
                <UI.FilterCollapse key={filter.code} filter={filter} onChange={onFilterChange} />
              ))}
              {otherFilters.map(filter => (
                <UI.FilterCollapse key={filter.code} filter={filter} onChange={onFilterChange} />
              ))}
              {checkboxFilters.map(filter => (
                <UI.FilterCollapse key={filter.code} filter={filter} onChange={onFilterChange} />
              ))}
            </UI.FiltersList>

            <UI.Actions>
              <UI.ApplyButton as={ApplyButton}>Применить</UI.ApplyButton>

              {selectedFilters.length > 0 && (
                <UI.ClearButton onClick={onClearAll}>
                  <X size={16} />
                  <span>Сбросить всё</span>
                </UI.ClearButton>
              )}
            </UI.Actions>
          </>
        )}
      </UI.Container>
    </UI.Wrapper>
  )
}

interface ApplyButtonProps extends React.PropsWithChildren {
  className?: string
}

const ApplyButton = observer(({children, className}: ApplyButtonProps) => {
  const router = useRouter()
  const vm = useFilters()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const applyFilters = useCallback(() => {
    navigateToFilters(vm, router, pathname, searchParams)
  }, [pathname, searchParams])

  return (
    <Button className={className} onClick={applyFilters}>
      {children}
    </Button>
  )
})
